/**
 * Page de gestion des utilisateurs - React Vite
 * Utilise le store Zustand via le hook useUserAdmin
 * Toute la logique metier est deleguee aux Use Cases via le store
 */

import { useState } from 'react';
import { Download, RefreshCw, AlertTriangle, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useUserAdmin } from '../hooks/useUserAdmin';
import { UserStatsCards } from '../components/UserStatsCards';
import { UserFilters } from '../components/UserFilters';
import { UserTable } from '../components/UserTable';
import { Pagination } from '../components/Pagination';
import {
  DeleteUserModal,
  ChangeRoleModal,
  EditUserModal,
} from '../components/UserModals';
import type { UserAdminDTO, UserRole, MettreAJourUtilisateurData } from '../../application/dto/dtos';

export function UsersPage() {
  const navigate = useNavigate();
  const store = useUserAdmin();

  // Modals
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; user: UserAdminDTO | null }>({
    isOpen: false,
    user: null,
  });
  const [roleModal, setRoleModal] = useState<{ isOpen: boolean; user: UserAdminDTO | null }>({
    isOpen: false,
    user: null,
  });
  const [editModal, setEditModal] = useState<{ isOpen: boolean; user: UserAdminDTO | null }>({
    isOpen: false,
    user: null,
  });

  const handleView = (userId: string) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleEdit = (userId: string) => {
    const user = store.users.find(u => u.idUtilisateur === userId);
    if (user) {
      setEditModal({ isOpen: true, user });
    }
  };

  const handleDelete = (userId: string) => {
    const user = store.users.find(u => u.idUtilisateur === userId);
    if (user) {
      setDeleteModal({ isOpen: true, user });
    }
  };

  const handleToggleActive = async (userId: string, activate: boolean) => {
    try {
      await store.toggleActive(userId, activate);
    } catch {
      // Error already handled in store
    }
  };

  const handleChangeRole = (userId: string) => {
    const user = store.users.find(u => u.idUtilisateur === userId);
    if (user) {
      setRoleModal({ isOpen: true, user });
    }
  };

  const handleLock = async (userId: string) => {
    try {
      await store.lockUser(userId);
    } catch {
      // Error already handled in store
    }
  };

  const handleUnlock = async (userId: string) => {
    try {
      await store.unlockUser(userId);
    } catch {
      // Error already handled in store
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal.user) return;
    try {
      await store.deleteUser(deleteModal.user.idUtilisateur);
      setDeleteModal({ isOpen: false, user: null });
    } catch {
      // Error already handled in store
    }
  };

  const confirmChangeRole = async (newRole: UserRole) => {
    if (!roleModal.user) return;
    try {
      await store.changeRole(roleModal.user.idUtilisateur, newRole);
      setRoleModal({ isOpen: false, user: null });
    } catch {
      // Error already handled in store
    }
  };

  const confirmEdit = async (updatedData: MettreAJourUtilisateurData) => {
    if (!editModal.user) return;
    try {
      await store.updateUser(editModal.user.idUtilisateur, updatedData);
      setEditModal({ isOpen: false, user: null });
    } catch {
      // Error already handled in store
    }
  };

  return (
    <div className="space-y-6">
      {/* Erreur globale */}
      {store.error && (
        <div className="flex items-center gap-2 p-4 bg-danger-light text-danger rounded-lg">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{store.error}</span>
        </div>
      )}

      {/* Stats */}
      <UserStatsCards stats={store.stats} loading={store.statsLoading} />

      {/* Header avec actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-content">
            Liste des utilisateurs
          </h2>
          <p className="text-sm text-content-muted">
            {store.totalItems} utilisateur{store.totalItems > 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/users/new')}
            className="px-4 py-2 bg-success text-content-inverted rounded-md hover:bg-success/90
                     transition-colors font-medium text-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouvel utilisateur
          </button>

          <button
            onClick={() => store.fetchUsers()}
            disabled={store.loading}
            className="p-2 text-content-muted hover:bg-surface-overlay
                     rounded-md transition-colors disabled:opacity-50"
            title="Actualiser"
          >
            <RefreshCw className={`w-5 h-5 ${store.loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => store.exportUsers()}
            className="px-4 py-2 bg-primary text-content-inverted rounded-md hover:bg-primary-hover
                     transition-colors font-medium text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exporter CSV
          </button>
        </div>
      </div>

      {/* Filtres */}
      <UserFilters
        filters={store.filters}
        onFilterChange={store.setFilters}
        onReset={store.resetFilters}
      />

      {/* Tableau */}
      <UserTable
        users={store.users}
        loading={store.loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
        onChangeRole={handleChangeRole}
        onLock={handleLock}
        onUnlock={handleUnlock}
      />

      {/* Pagination */}
      {store.totalPages > 1 && (
        <Pagination
          currentPage={store.currentPage}
          totalPages={store.totalPages}
          totalItems={store.totalItems}
          itemsPerPage={store.itemsPerPage}
          onPageChange={store.setCurrentPage}
        />
      )}

      {/* Modals */}
      <DeleteUserModal
        user={deleteModal.user}
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, user: null })}
        onConfirm={confirmDelete}
      />

      <ChangeRoleModal
        user={roleModal.user}
        isOpen={roleModal.isOpen}
        onClose={() => setRoleModal({ isOpen: false, user: null })}
        onConfirm={confirmChangeRole}
      />

      <EditUserModal
        user={editModal.user}
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, user: null })}
        onConfirm={confirmEdit}
        organisations={[...new Set(store.users.map(u => u.organisation).filter(Boolean) as string[])]}
      />
    </div>
  );
}

export default UsersPage;
